import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, Zap, MessageSquare, GitMerge, ChevronDown } from 'lucide-react';

export default function Landing() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const handleGetStarted = () => {
    navigate('/dashboard');
  };

  const features = [
    {
      icon: <Zap className="w-6 h-6" />,
      title: "3 IA en parallèle",
      description: "GPT-4o, Claude et Gemini répondent simultanément à vos questions.",
      color: "#10B981"
    },
    {
      icon: <MessageSquare className="w-6 h-6" />,
      title: "Débat intelligent",
      description: "Faites échanger les IA entre elles pour obtenir des réponses plus nuancées.",
      color: "#3B82F6"
    },
    {
      icon: <GitMerge className="w-6 h-6" />,
      title: "Synthèse finale",
      description: "Obtenez une réponse enrichie par l'intelligence collective des meilleures IA.",
      color: "#F97316"
    }
  ];

  const aiLogos = [
    { name: 'GPT-4o', color: '#10B981' },
    { name: 'Claude', color: '#F97316' },
    { name: 'Gemini', color: '#3B82F6' }
  ];

  return (
    <div className="min-h-screen bg-[#05050A] overflow-hidden" data-testid="landing-page">
      {/* Ambient background */}
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-500/5 via-transparent to-blue-500/5" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-[120px] animate-pulse-glow" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-[120px] animate-pulse-glow" style={{ animationDelay: '1s' }} />
      </div>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-3"
          >
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-400 to-blue-500 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="font-outfit font-bold text-xl text-white">Synapsea</span>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-4"
          >
            {!isAuthenticated && (
              <button
                onClick={() => navigate('/login')}
                className="btn-secondary text-sm"
                data-testid="nav-login-btn"
              >
                Connexion
              </button>
            )}
            <button
              onClick={handleGetStarted}
              className="btn-primary text-sm"
              data-testid="nav-get-started-btn"
            >
              {isAuthenticated ? 'Dashboard' : 'Essayer'}
              <ArrowRight className="w-4 h-4" />
            </button>
          </motion.div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative z-10 min-h-screen flex items-center justify-center px-4 sm:px-6 pt-20 pb-10">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full glass-light mb-6 sm:mb-8"
            >
              <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
              <span className="text-xs sm:text-sm text-white/70">Questions illimitées pour les tests</span>
            </motion.div>

            {/* Main heading */}
            <h1 className="font-outfit font-black text-3xl sm:text-5xl lg:text-7xl tracking-tight leading-[1.1] mb-4 sm:mb-6 px-2">
              <span className="text-white">Une question.</span>
              <br />
              <span className="bg-gradient-to-r from-emerald-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">
                Trois intelligences.
              </span>
              <br />
              <span className="text-white">Une réponse affinée.</span>
            </h1>

            <p className="font-manrope text-base sm:text-lg lg:text-xl text-white/60 max-w-2xl mx-auto mb-8 sm:mb-10 leading-relaxed px-4">
              Interrogez simultanément les meilleures IA du marché. 
              Comparez leurs réponses. Faites-les débattre. 
              Obtenez la synthèse ultime.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 mb-8 sm:mb-12 px-4">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleGetStarted}
                className="btn-glow text-white font-semibold px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg w-full sm:w-auto"
                data-testid="hero-get-started-btn"
              >
                <Sparkles className="w-5 h-5 mr-2 inline" />
                Commencer gratuitement
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
                className="btn-secondary px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg w-full sm:w-auto"
                data-testid="hero-learn-more-btn"
              >
                En savoir plus
              </motion.button>
            </div>

            {/* AI Logos */}
            <div className="flex flex-wrap justify-center gap-3 sm:gap-6 items-center px-2">
              {aiLogos.map((ai, index) => (
                <motion.div
                  key={ai.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + index * 0.1 }}
                  className="flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full glass-light"
                >
                  <div 
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: ai.color, boxShadow: `0 0 10px ${ai.color}` }}
                  />
                  <span className="text-sm text-white/80 font-medium">{ai.name}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Scroll indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5 }}
            className="absolute bottom-8 left-1/2 -translate-x-1/2"
          >
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ repeat: Infinity, duration: 2 }}
              className="text-white/30"
            >
              <ChevronDown className="w-6 h-6" />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="relative z-10 py-16 sm:py-32 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10 sm:mb-16"
          >
            <span className="label text-emerald-400 mb-4 block">Fonctionnalités</span>
            <h2 className="font-outfit font-bold text-2xl sm:text-4xl lg:text-5xl text-white mb-4">
              Comment ça marche
            </h2>
            <p className="text-white/50 max-w-xl mx-auto text-sm sm:text-base px-4">
              Une expérience unique pour exploiter le meilleur de chaque IA
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass-card p-5 sm:p-8 rounded-2xl group"
                data-testid={`feature-card-${index}`}
              >
                <div 
                  className="w-12 sm:w-14 h-12 sm:h-14 rounded-xl flex items-center justify-center mb-4 sm:mb-6 transition-transform duration-300 group-hover:scale-110"
                  style={{ 
                    backgroundColor: `${feature.color}15`,
                    boxShadow: `0 0 30px ${feature.color}30`
                  }}
                >
                  <div style={{ color: feature.color }}>{feature.icon}</div>
                </div>
                <h3 className="font-outfit font-bold text-lg sm:text-xl text-white mb-2 sm:mb-3">
                  {feature.title}
                </h3>
                <p className="text-white/50 leading-relaxed text-sm sm:text-base">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-10 py-16 sm:py-32 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="animated-border p-6 sm:p-12 rounded-2xl sm:rounded-3xl text-center"
          >
            <h2 className="font-outfit font-bold text-2xl sm:text-3xl lg:text-4xl text-white mb-4">
              Prêt à explorer ?
            </h2>
            <p className="text-white/50 mb-6 sm:mb-8 max-w-lg mx-auto text-sm sm:text-base px-2">
              Rejoignez des milliers d'utilisateurs qui exploitent déjà la puissance combinée des meilleures IA.
            </p>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleGetStarted}
              className="btn-glow text-white font-semibold px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg"
              data-testid="cta-get-started-btn"
            >
              Commencer maintenant
              <ArrowRight className="w-5 h-5 ml-2 inline" />
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/5">
        <div className="py-8 px-4 sm:px-6">
          <div className="max-w-6xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-400 to-blue-500 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <span className="font-outfit font-bold text-white">Synapsea</span>
            </div>
            <p className="text-sm text-white/30">
              © 2025 Synapsea. Tous droits réservés.
            </p>
          </div>
        </div>
        {/* Powered By MarketPulse */}
        <div className="powered-by">
          <span className="powered-by-text">Powered By</span>
          <span className="powered-by-brand">MarketPulse</span>
        </div>
      </footer>
    </div>
  );
}
